import { Temporal } from 'temporal-polyfill';

import type { CalendarView, ConcreteEvent } from './session-schedule-calendar-types.js';

export type ScheduleXDateRange = {
  start: Temporal.ZonedDateTime;
  end: Temporal.ZonedDateTime;
};

export const SCHEDULE_X_VIEW_LABELS: Record<CalendarView, string> = {
  month: 'Month',
  week: 'Week',
  day: 'Day',
};

/** Quarter-hour steps in week/day views (matches legacy RBC `step={15}`). */
export const SCHEDULE_X_GRID_STEP_MINUTES = 15;

/** Event cards shown in each month cell before "+ N events". */
export const SCHEDULE_X_MONTH_GRID_VISIBLE_EVENTS = 2;

/**
 * Schedule-X month slice size. Coach sessions are single-day, so levels are real
 * events (not week blockers) and this can match {@link SCHEDULE_X_MONTH_GRID_VISIBLE_EVENTS}.
 * Rendering only 2 slots avoids a 4→2 flash that CSS/JS post-processing cannot prevent.
 */
export const SCHEDULE_X_MONTH_GRID_N_EVENTS_PER_DAY = SCHEDULE_X_MONTH_GRID_VISIBLE_EVENTS;

/**
 * Scrollable time-grid height. Schedule-X sets each grid-step row to `gridHeight / slotsPerDay`.
 * 2640px ≈ 55px per 30-minute block — same minimum as legacy `.rbc-timeslot-group`.
 */
export const SCHEDULE_X_WEEK_GRID_HEIGHT_PX = ((24 * 60) / SCHEDULE_X_GRID_STEP_MINUTES / 2) * 55;

export function toScheduleXViewName(view: CalendarView): string {
  switch (view) {
    case 'month':
      return 'month-grid';
    case 'week':
      return 'week';
    case 'day':
      return 'day';
  }
}

/**
 * Fallback toolbar title before Schedule-X `onRangeUpdate` (or when range is unavailable).
 * Week label uses Sunday-start civil week in the coach timezone.
 */
export function formatToolbarLabelFromActiveInstant(
  activeInstant: Temporal.Instant,
  timeZone: string,
  view: CalendarView,
): string {
  const anchor = activeInstant.toZonedDateTimeISO(timeZone).toPlainDate();

  if (view === 'month') {
    return anchor.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  }

  if (view === 'day') {
    return anchor.toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }

  const daysFromSunday = anchor.dayOfWeek === 7 ? 0 : anchor.dayOfWeek;
  const weekStart = anchor.subtract({ days: daysFromSunday });
  const weekEnd = weekStart.add({ days: 6 });
  const startLabel = weekStart.toLocaleString('en-US', { month: 'short', day: 'numeric' });
  const endLabel = weekEnd.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${startLabel} – ${endLabel}`;
}

/** Sessions per civil day in the coach timezone (matches day-view drill-down from "+ N events"). */
export function coachCalendarEventCountByDate(events: readonly ConcreteEvent[], timeZone: string): Map<string, number> {
  const counts = new Map<string, number>();

  for (const event of events) {
    const start = event.startDate;
    const end = event.eventInterval.end.toZonedDateTimeISO(timeZone).toPlainDate();

    for (let cursor = start; Temporal.PlainDate.compare(cursor, end) <= 0; cursor = cursor.add({ days: 1 })) {
      const key = cursor.toString();
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }

  return counts;
}

export function formatScheduleXMonthGridEventsMoreLabel(hiddenCount: number): string {
  return hiddenCount === 1 ? '+ 1 event' : `+ ${hiddenCount} events`;
}

let scheduleXMonthGridSyncFlushPending = false;
let scheduleXMonthGridSyncLatestRun: (() => void) | null = null;

/**
 * Month-grid label sync runs after Schedule-X paints and React custom cells mount.
 * Visibility is capped in CSS; this only updates `hidden` attributes and "+ N events" text.
 */
export function flushScheduleXMonthGridSync(run: () => void): void {
  scheduleXMonthGridSyncLatestRun = run;
  if (scheduleXMonthGridSyncFlushPending) {
    return;
  }
  scheduleXMonthGridSyncFlushPending = true;

  requestAnimationFrame(() => {
    scheduleXMonthGridSyncLatestRun?.();
    scheduleXMonthGridSyncFlushPending = false;
    scheduleXMonthGridSyncLatestRun = null;
  });
}

function syncScheduleXMonthGridVisibleEventCap(
  dayCell: HTMLElement,
  maxVisible: number = SCHEDULE_X_MONTH_GRID_VISIBLE_EVENTS,
): void {
  const eventEls = dayCell.querySelectorAll<HTMLElement>('.sx__month-grid-day__events .sx__month-grid-event');
  const sessionEventEls: HTMLElement[] = [];

  for (const el of eventEls) {
    if (!el.querySelector('[data-session-schedule-event-id]')) {
      // Week-level spacer slots from Schedule-X — hide so real sessions get the visible cap.
      el.hidden = true;
      continue;
    }
    sessionEventEls.push(el);
  }

  sessionEventEls.forEach((el, index) => {
    el.hidden = index >= maxVisible;
  });
}

function syncScheduleXMonthGridEventsMoreLabel(
  dayCell: HTMLElement,
  dateStr: string,
  eventCountByDate: ReadonlyMap<string, number>,
): void {
  const total = eventCountByDate.get(dateStr) ?? 0;
  const visible = dayCell.querySelectorAll(
    '.sx__month-grid-day__events .sx__month-grid-event:not([hidden]) [data-session-schedule-event-id]',
  ).length;
  const hidden = total - visible;

  const button = dayCell.querySelector<HTMLButtonElement>('.sx__month-grid-day__events-more');
  if (!button) {
    return;
  }

  if (hidden <= 0) {
    button.hidden = true;
    return;
  }

  button.hidden = false;
  const label = formatScheduleXMonthGridEventsMoreLabel(hidden);
  if (button.textContent !== label) {
    button.textContent = label;
  }

  const ariaLabel = hidden === 1 ? `Link to 1 more event on ${dateStr}` : `Link to ${hidden} more events on ${dateStr}`;
  if (button.getAttribute('aria-label') !== ariaLabel) {
    button.setAttribute('aria-label', ariaLabel);
  }
}

/**
 * Month grid post-render: align `hidden` attributes and "+ N events" labels with the 2-card cap.
 */
export function syncScheduleXMonthGridDayCells(root: HTMLElement, eventCountByDate: ReadonlyMap<string, number>): void {
  for (const dayCell of root.querySelectorAll<HTMLElement>('.sx__month-grid-day[data-date]')) {
    const dateStr = dayCell.dataset.date;
    if (!dateStr) {
      continue;
    }

    syncScheduleXMonthGridVisibleEventCap(dayCell);
    syncScheduleXMonthGridEventsMoreLabel(dayCell, dateStr, eventCountByDate);
  }
}

/**
 * Month-grid ranges start on the week containing the 1st, so `range.start` can be in the previous
 * month when the 1st is not the first day of the week.
 * `range.start` is at most 6 days before the 1st, so anchoring on `start + 7 days` always lands
 * inside the month the grid is showing.
 */
const MONTH_GRID_ANCHOR_OFFSET = { days: 7 };

export function formatScheduleXRangeLabel(range: ScheduleXDateRange, view: CalendarView): string {
  const start = range.start.toPlainDate();
  const end = range.end.toPlainDate();

  if (view === 'month') {
    return start.add(MONTH_GRID_ANCHOR_OFFSET).toLocaleString('en-US', { month: 'long', year: 'numeric' });
  }

  if (view === 'week') {
    const startLabel = start.toLocaleString('en-US', { month: 'short', day: 'numeric' });
    const endLabel = end.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startLabel} – ${endLabel}`;
  }

  return start.toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}
